import api from '@/services/api'
import { can } from '@/config/permissions'
import { getDraft, putDraft, listDrafts, ownerKey, refreshDraftCount } from './drafts'
import { sendDraft, draftRequest, draftPermission } from './sendDraft'
import { online, visible, frozen, syncing } from './runtime'
import { reportPwaError } from './diagnostics'

// F13.4 outbox: an action confirmed while offline is queued with the time it
// was done on the device and sent later, oldest first, with its Idempotency-Key
// (a replay never applies twice). Only these non-critical kinds can be queued;
// every other write needs a connection (api.js rejects writes while offline).
export const OFFLINE_KINDS = ['assessment', 'pen-feeding', 'alert', 'task']
// Waiting to be (re)sent. 'uncertain' = sent but no answer; 'sending' left over
// when the app was closed mid-request. Both replay safely with the same key.
const PENDING = ['queued', 'uncertain', 'sending']

let seq = 0
const order = d => d.queueSeq ?? Date.parse(d.occurredAt || d.updatedAt)

/** The request never got an answer from the server (offline, timeout, guard in api.js). */
export const isNetworkError = e => e?.code === 'SF_OFFLINE' || (!!e?.isAxiosError && !e.response)

/**
 * Queue a draft for ordered sync. The action time is fixed now and added to the
 * payload once; a draft that may already have reached the server keeps its
 * payload untouched so the replay matches the first attempt.
 */
export async function queueDraft(id) {
  const d = await getDraft(id)
  if (!d) throw Error('Không tìm thấy bản nháp của tài khoản này.')
  if (!OFFLINE_KINDS.includes(d.kind)) throw Error('Thao tác này cần có mạng, không lưu chờ đồng bộ được.')
  if (d.status === 'sent' || PENDING.includes(d.status)) return d
  const occurredAt = new Date().toISOString()
  const row = await putDraft({ ...d, status: 'queued', error: '', occurredAt, queueSeq: Date.now() * 1000 + (seq++ % 1000), payload: { ...d.payload, occurred_at: occurredAt } })
  scheduleSync(0)
  return row
}

/**
 * What a business screen calls on "Lưu/Gửi": send now when online, otherwise
 * (or when the connection drops mid-request) queue it. Server-side rejections
 * still throw so the screen can show them, as before.
 * Resolves { queued: true, draft } or { queued: false, result }.
 */
export async function submitDraft(id) {
  if (!online.value) return { queued: true, draft: await queueDraft(id) }
  try {
    return { queued: false, result: await sendDraft(id) }
  } catch (e) {
    if (!isNetworkError(e)) throw e
    return { queued: true, draft: await queueDraft(id) }
  }
}

let running = null
let timer = null
let delay = 15000

/** Send every pending action of the signed-in account, oldest first. Single-flight. */
export function syncOutbox() {
  return running ||= run().finally(() => { running = null })
}

function scheduleSync(ms = delay) {
  clearTimeout(timer)
  timer = setTimeout(() => { timer = null; syncOutbox() }, ms)
}

async function run() {
  const owner = ownerKey()
  if (!online.value || frozen.value || !owner) return
  const items = (await listDrafts(owner)).filter(d => PENDING.includes(d.status)).sort((a, b) => order(a) - order(b))
  if (!items.length) return
  syncing.value = true
  try {
    const me = (await api.get('/me')).data.data
    if (!me || `${me.farm_id}:${me.id}` !== owner) return
    let perms = me.permissions
    if (!perms) { try { perms = JSON.parse(localStorage.getItem('user') || '{}').permissions } catch { perms = null } }
    for (const d of items) {
      if (!online.value || frozen.value || ownerKey() !== owner) { scheduleSync(); return }
      if (await sendQueued(d, perms) === 'retry') { scheduleSync(); delay = Math.min(delay * 2, 300000); return }
    }
    delay = 15000
  } catch (e) {
    if (isNetworkError(e) || e?.response?.status >= 500) scheduleSync()
  } finally {
    syncing.value = false
    await refreshDraftCount()
  }
}

// One queued action. Server rejections (4xx) park it as 'review' and the run
// goes on; no answer / 5xx / 401 / 429 keeps it queued and stops the run so
// later actions never overtake it.
async function sendQueued(d, perms) {
  if (!can(perms, draftPermission(d), 'G')) {
    await putDraft({ ...d, status: 'review', error: 'Bạn không còn quyền gửi thao tác này. Vui lòng liên hệ quản lý.' })
    return 'next'
  }
  await putDraft({ ...d, status: 'sending' })
  const { body, headers } = draftRequest(d)
  try {
    const result = (await api.post(d.endpoint, body, { headers })).data.data
    await putDraft({ ...d, status: 'sent', result, file: null, error: '', syncedAt: new Date().toISOString() })
    return 'next'
  } catch (e) {
    reportPwaError('SF_OUTBOX_SEND_FAILED')
    const status = e.response?.status
    if (!status || status >= 500 || status === 401 || status === 429) {
      await putDraft({ ...d, status: d.status === 'queued' ? 'queued' : 'uncertain', error: e.message })
      return 'retry'
    }
    await putDraft({ ...d, status: 'review', error: e.message })
    return 'next'
  }
}

/** Put a 'review' action back in the queue (same payload and key). */
export async function retryDraft(id) {
  const d = await getDraft(id)
  if (!d || d.status !== 'review') return
  await putDraft({ ...d, status: 'queued', error: '' })
  return syncOutbox()
}

let wired = false
/** Sync when the connection comes back, the app is reopened, and every minute while anything waits. */
export function initOutbox() {
  if (wired || typeof window === 'undefined') return
  wired = true
  window.addEventListener('online', () => { delay = 15000; scheduleSync(1000) })
  document.addEventListener('visibilitychange', () => { if (visible.value) syncOutbox() })
  setInterval(() => { if (visible.value && !timer) syncOutbox() }, 60000)
  syncOutbox()
}
